if (!customElements.get('dignity-mobile-experience')) {
  class DignityMobileExperience extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
        return;
      }

      this.initialized = true;
      this.isProduct = this.dataset.product === 'true';
      this.action = this.querySelector('[data-dme-product-action]');
      this.actionLabel = this.querySelector('[data-dme-product-label]');
      this.overlay = this.querySelector('[data-dme-size-overlay]');
      this.drawer = this.querySelector('.dme-size-drawer');
      // Not `this.title`: HTMLElement already owns that property, so assigning
      // an element to it silently stores a string and the heading never updates.
      this.drawerTitle = this.querySelector('[data-dme-drawer-title]');
      this.stepSize = this.querySelector('[data-dme-step="size"]');
      this.stepPurchase = this.querySelector('[data-dme-step="purchase"]');
      this.purchaseHost = this.querySelector('[data-dme-purchase-host]');
      this.selectionSize = this.querySelector('[data-dme-selection-size]');
      this.options = this.querySelector('[data-dme-size-options]');
      this.confirm = this.querySelector('[data-dme-size-confirm]');
      // Selection is tracked by option VALUE, never by element id: the theme
      // re-renders the variant picker on every change and replaces those nodes.
      this.selectedSize = null;
      this.lastFocusedElement = null;
      this.lockedScrollY = 0;
      this.reopenAfterHelp = false;
      this.step = 'size';
      this.cartSurfaceObservers = [];
      this.sizeLabel = this.dataset.sizeLabel || 'Select Size';
      this.addLabel = this.dataset.addLabel || 'Add to Cart';
      this.continueLabel = this.dataset.continueLabel || 'Continue';

      this.bindGlobalActions();
      this.bindScrollState();
      this.bindWishlist();
      this.bindCartCount();
      if (this.isProduct) this.bindProductActions();
    }

    disconnectedCallback() {
      this.productObserver?.disconnect();
      this.cartObserver?.disconnect();
      this.cartSurfaceObservers?.forEach((observer) => observer.disconnect());
    }

    clickOriginal(selectors) {
      const source = document.querySelector(selectors);
      if (!source) return false;
      source.click();
      return true;
    }

    openPanel(panel) {
      if (!panel) return;
      panel.hidden = false;
      document.body.classList.add('dme-panel-open');
      panel.querySelector('input, button, a')?.focus();
    }

    closePanels() {
      this.querySelectorAll('.dme-panel').forEach((panel) => {
        panel.hidden = true;
      });
      document.body.classList.remove('dme-panel-open');
    }

    buildMenu() {
      const target = this.querySelector('[data-dme-menu-content]');
      const source = document.querySelector('.section-header .menu-drawer__navigation-container');
      if (!target || !source || target.childElementCount) return;
      const clone = source.cloneNode(true);
      clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      clone.querySelectorAll('[aria-controls]').forEach((node) => node.removeAttribute('aria-controls'));
      target.appendChild(clone);
    }

    bindGlobalActions() {
      this.querySelector('[data-dme-search]')?.addEventListener('click', () => {
        this.openPanel(this.querySelector('[data-dme-search-panel]'));
      });
      this.querySelector('[data-dme-cart]')?.addEventListener('click', () => {
        this.clickOriginal('.section-header #cart-icon-bubble, .section-header a.header__icon--cart');
      });
      this.querySelector('[data-dme-menu]')?.addEventListener('click', (event) => {
        this.buildMenu();
        this.openPanel(this.querySelector('[data-dme-menu-panel]'));
        event.currentTarget.setAttribute('aria-expanded', 'true');
      });
      this.querySelectorAll('[data-dme-panel-close]').forEach((button) => {
        button.addEventListener('click', () => this.closePanels());
      });
      this.querySelector('[data-dme-filter]')?.addEventListener('click', () => {
        this.clickOriginal('.mobile-facets__open-wrapper, .mobile-facets__open');
      });
    }

    bindScrollState() {
      const update = () => document.body.classList.toggle('dme-away-from-top', window.scrollY > 4);
      update();
      window.addEventListener('scroll', update, { passive: true });
    }

    wishlistCountFromDom() {
      const candidates = document.querySelectorAll('.swym-wishlist-header-counter, .swym-header-launcher-count, [data-swym-wishlist-count]');
      for (const node of candidates) {
        const value = Number.parseInt(node.textContent || node.getAttribute('data-count') || '0', 10);
        if (Number.isFinite(value) && value > 0) return value;
      }
      return 0;
    }

    bindWishlist() {
      const save = this.querySelector('[data-dme-save]');
      if (!save) return;
      const badge = save.querySelector('[data-dme-save-count]');
      const apply = (count) => {
        const n = Number(count) || 0;
        save.hidden = n === 0;
        if (badge) {
          badge.textContent = n > 99 ? '99+' : String(n);
          badge.classList.toggle('dme-cart-count--empty', n === 0);
        }
        save.setAttribute('aria-label', n ? `Wishlist, ${n} saved` : 'Wishlist');
        // The desktop header carries the same count beside its wishlist icon.
        document.querySelectorAll('[data-dignity-wishlist-count]').forEach((bubble) => {
          bubble.hidden = n === 0;
          const figure = bubble.querySelector('[aria-hidden="true"]') || bubble;
          figure.textContent = n > 99 ? '99+' : String(n);
        });
      };
      let swat = null;
      const refresh = () => {
        const api = swat || window._swat;
        if (api && typeof api.fetch === 'function') {
          try {
            api.fetch((items) => apply(Array.isArray(items) ? items.length : 0));
            return;
          } catch (error) {
            /* fall back to whatever Swym has painted */
          }
        }
        const domCount = this.wishlistCountFromDom();
        if (domCount > 0) apply(domCount);
      };
      const ready = (api) => {
        if (!api || swat) return;
        swat = api;
        refresh();
        try {
          const events = api.JSEvents || {};
          const layer = api.evtLayer;
          if (layer && typeof layer.addEventListener === 'function') {
            ['addedToWishlist', 'removedFromWishlist', 'listContentsChanged'].forEach((key) => {
              if (events[key]) layer.addEventListener(events[key], refresh);
            });
          }
        } catch (error) {
          /* events are a bonus; the bookmark also announces its own changes */
        }
      };
      if (window._swat && typeof window._swat.fetch === 'function') ready(window._swat);
      window.SwymCallbacks = window.SwymCallbacks || [];
      window.SwymCallbacks.push(ready);
      document.addEventListener('dignity:wishlist-changed', refresh);
      setTimeout(refresh, 1500);
      setTimeout(refresh, 4000);
    }

    bindCartCount() {
      const update = () => {
        const source = document.querySelector('.section-header #cart-icon-bubble .cart-count-bubble span[aria-hidden="true"], .section-header .cart-count-bubble span');
        const count = Number.parseInt(source?.textContent || '0', 10) || 0;
        // A rising count is proof something reached the cart, whoever wrote it.
        if (this.lastCartCount !== undefined && count > this.lastCartCount) this.onCartChanged();
        this.lastCartCount = count;
        const target = this.querySelector('[data-dme-cart-count]');
        if (!target) return;
        target.textContent = String(count);
        target.classList.toggle('dme-cart-count--empty', count === 0);
      };
      update();
      document.addEventListener('cart:refresh', update);
      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, update);
      }
      const cartSource = document.querySelector('.section-header #cart-icon-bubble');
      if (cartSource) {
        this.cartObserver = new MutationObserver(update);
        this.cartObserver.observe(cartSource, { childList: true, subtree: true, characterData: true });
      }
    }

    /* ---------------------------------------------------------------------
       Product bindings. Every lookup below is resolved fresh on each call.
       The theme replaces the variant picker, the price and the submit button
       on every variant change, so nothing may be cached across a render.
    --------------------------------------------------------------------- */

    productRoot() {
      return document.querySelector('product-info[id^="MainProduct-"]')
        || document.querySelector('product-info')
        || document.querySelector('[id^="ProductInfo-"]')
        || document;
    }

    sizeFieldset() {
      const root = this.productRoot();
      const direct = root.querySelector('fieldset.dl-option--size');
      if (direct) return direct;
      const candidates = [
        ...root.querySelectorAll('variant-selects fieldset'),
        ...root.querySelectorAll('fieldset.product-form__input'),
      ];
      return candidates.find((fieldset) => {
        if (!fieldset.querySelector('input[type="radio"]')) return false;
        const legend = fieldset.querySelector('legend, .form__label');
        return /\bsize\b/i.test(legend?.textContent || '');
      }) || null;
    }

    sizeInputs() {
      return [...(this.sizeFieldset()?.querySelectorAll('input[type="radio"]') || [])];
    }

    inputDisabled(input) {
      if (!input) return true;
      if (input.disabled || input.classList.contains('disabled')) return true;
      const label = input.id
        ? this.sizeFieldset()?.querySelector(`label[for="${CSS.escape(input.id)}"]`)
        : null;
      return Boolean(label?.classList.contains('disabled'));
    }

    productForm() {
      const input = this.sizeInputs()[0];
      // Radios carry form="product-form-<section id>", so the element's own
      // form property is the most reliable handle we can get.
      if (input?.form) return input.form;
      const root = this.productRoot();
      return root.querySelector('form[action*="/cart/add"]') || null;
    }

    variantInput() {
      return this.productForm()?.querySelector('input[name="id"]') || null;
    }

    originalSubmit() {
      const form = this.productForm();
      const inForm = form?.querySelector('[type="submit"][name="add"], .product-form__submit');
      if (inForm) return inForm;
      const root = this.productRoot();
      return root.querySelector('product-form .product-form__submit:not([data-dme-product-action])')
        || root.querySelector('[id^="ProductSubmitButton-"]')
        || null;
    }

    checkedSizeValue() {
      const checked = this.sizeFieldset()?.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : null;
    }

    rebuildSizes() {
      if (!this.options) return false;
      const fieldset = this.sizeFieldset();
      const inputs = this.sizeInputs();
      if (!fieldset || !inputs.length) return false;

      this.options.replaceChildren();
      inputs.forEach((input) => {
        const sourceLabel = input.id
          ? fieldset.querySelector(`label[for="${CSS.escape(input.id)}"]`)
          : null;
        const value = input.value;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'dme-size-choice';
        button.dataset.sizeValue = value;
        button.disabled = this.inputDisabled(input);
        button.setAttribute('aria-pressed', String(value === this.selectedSize));
        button.textContent = (sourceLabel?.textContent || value).trim();
        this.options.appendChild(button);
      });
      return true;
    }

    markSizes() {
      this.options?.querySelectorAll('.dme-size-choice').forEach((choice) => {
        choice.setAttribute('aria-pressed', String(choice.dataset.sizeValue === this.selectedSize));
      });
    }

    lockScroll() {
      this.lockedScrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.top = `-${this.lockedScrollY}px`;
      document.body.classList.add('dme-size-open');
    }

    unlockScroll() {
      if (!document.body.classList.contains('dme-size-open')) return;
      document.body.classList.remove('dme-size-open');
      document.body.style.top = '';
      // Restore the reading position the customer had before the drawer opened.
      window.scrollTo(0, this.lockedScrollY);
    }

    openSizes() {
      if (!this.overlay || !this.rebuildSizes()) return;
      this.lastFocusedElement = document.activeElement;
      this.overlay.hidden = false;
      this.overlay.setAttribute('aria-hidden', 'false');
      this.lockScroll();
      this.syncProductState();
      requestAnimationFrame(() => this.drawer?.focus({ preventScroll: true }));
    }

    hideOverlay() {
      this.overlay.hidden = true;
      this.overlay.setAttribute('aria-hidden', 'true');
      this.unlockScroll();
    }

    closeSizes() {
      if (!this.overlay || this.overlay.hidden) return;
      // Closing adds nothing: a customer who backs out leaves with exactly
      // what he had before he opened the drawer.
      this.showSizeStep();
      this.hideOverlay();
      this.lastFocusedElement?.focus?.({ preventScroll: true });
    }

    chooseSize(button) {
      if (!button || button.disabled) return;
      const value = button.dataset.sizeValue;
      const input = this.sizeInputs().find((candidate) => candidate.value === value);
      if (!input || this.inputDisabled(input)) return;

      this.selectedSize = value;
      // Remembered across the site: the collection's photo drawer opens on it
      // and the filter sheet offers it back as one tap. Storage throws in a
      // private window, so a failure here must not stop the purchase.
      try {
        window.localStorage.setItem('dignity:my-size', value);
      } catch (error) {
        /* a convenience, never a dependency */
      }
      this.markSizes();

      if (!input.checked) {
        // Click the real label so the theme's own variant handling runs exactly
        // as it does when a customer taps the inline picker.
        const label = input.id
          ? this.sizeFieldset()?.querySelector(`label[for="${CSS.escape(input.id)}"]`)
          : null;
        if (label) {
          label.click();
        } else {
          input.checked = true;
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      this.syncProductState();
      // The theme re-renders the section after a variant change; re-read once
      // it has settled so availability and the button state are truthful.
      setTimeout(() => this.syncProductState(), 120);
      setTimeout(() => this.syncProductState(), 500);
    }

    submitOriginal(attempt = 0) {
      const submit = this.originalSubmit();
      if (!submit) return;
      if (submit.disabled || submit.getAttribute('aria-disabled') === 'true') {
        if (attempt < 12) setTimeout(() => this.submitOriginal(attempt + 1), 75);
        return;
      }
      submit.click();
    }

    scheduleProductSync() {
      if (this.syncFrame) return;
      this.syncFrame = requestAnimationFrame(() => {
        this.syncFrame = null;
        if (!this.overlay?.hidden && this.step === 'size') this.rebuildSizes();
        this.syncProductState();
      });
    }

    syncProductState() {
      const fieldset = this.sizeFieldset();
      const variantInput = this.variantInput();
      const submit = this.originalSubmit();
      const checkedValue = this.checkedSizeValue();

      // A stale selection (size removed, or the section re-rendered into a
      // different option set) must never leave a dead button enabled.
      if (this.selectedSize && !this.sizeInputs().some((input) => input.value === this.selectedSize)) {
        this.selectedSize = null;
      }

      const sizeChosen = !fieldset || (Boolean(this.selectedSize) && checkedValue === this.selectedSize);
      const variantReady = Boolean(variantInput && !variantInput.disabled && variantInput.value);
      const submitReady = Boolean(submit && !submit.disabled && submit.getAttribute('aria-disabled') !== 'true');
      const ready = sizeChosen && variantReady && submitReady;

      // Where the theme renders a purchase block (.dl-purchase) the drawer has
      // a second step that carries it, so this button says Continue. On a
      // template without one the button is the add itself.
      const forwardLabel = this.purchaseNode() ? this.continueLabel : this.addLabel;
      if (this.actionLabel) this.actionLabel.textContent = ready ? forwardLabel : this.sizeLabel;
      if (this.action) this.action.dataset.mode = ready ? 'add' : 'size';
      if (this.confirm) {
        this.confirm.disabled = !ready;
        if (this.confirm.textContent !== forwardLabel) this.confirm.textContent = forwardLabel;
      }
      this.markSizes();
    }

    openSizeHelp() {
      const root = this.productRoot();
      const help = root.querySelector('modal-opener [id^="ProductPopup-"]')
        || root.querySelector('[id^="ProductPopup-"]')
        || root.querySelector('button.link[aria-controls^="PopupModal-"]')
        || root.querySelector('a[href*="size-chart"], a[href*="size-guide"]');
      if (!help) return;
      // The size guide is a modal-dialog and the drawer sits above everything,
      // so the drawer stands down while the guide is open and comes back when
      // it closes. The chosen size survives because it is held by value.
      this.reopenAfterHelp = true;
      this.closeSizes();
      // Let the drawer release the scroll lock before the popup takes it.
      requestAnimationFrame(() => help.click());
    }

    /* ---------------------------------------------------------------------
       Purchase step ("Your Selection"). The theme's own purchase block —
       Dawn's Add to Cart and GoKwik's Buy Now inside .dl-purchase — is MOVED
       into the drawer and moved back when the drawer closes. Moving keeps
       every listener Dawn and GoKwik attached; a clone would lose them.
       Bundle pricing is Shopify's automatic discounts, applied in the cart,
       so nothing here has to know about offers.
    --------------------------------------------------------------------- */

    purchaseNode() {
      const submit = this.originalSubmit();
      return submit?.closest('.dl-purchase') || document.querySelector('.dl-purchase') || null;
    }

    park(node) {
      if (!node || !node.parentNode) return null;
      const marker = document.createComment('dme-parked');
      node.parentNode.insertBefore(marker, node);
      this.purchaseHost.appendChild(node);
      return { node, marker };
    }

    unpark(entry) {
      if (!entry?.marker?.parentNode) return;
      entry.marker.parentNode.insertBefore(entry.node, entry.marker);
      entry.marker.remove();
    }

    showPurchaseStep() {
      const purchase = this.purchaseNode();
      if (!purchase || !this.purchaseHost || !this.stepPurchase) return false;

      this.parkedPurchase = this.park(purchase);
      this.step = 'purchase';
      this.stepSize.hidden = true;
      this.stepPurchase.hidden = false;
      // Step down the stacking order so GoKwik's checkout, which Buy Now opens
      // at its own z-index, can sit above the drawer.
      this.overlay.classList.add('dme-purchase-mode');
      if (this.drawerTitle && this.dataset.purchaseTitle) this.drawerTitle.textContent = this.dataset.purchaseTitle;
      if (this.selectionSize) this.selectionSize.textContent = this.selectedSize || '';
      if (this.drawer) this.drawer.scrollTop = 0;
      return true;
    }

    showSizeStep() {
      if (this.step !== 'purchase') return;
      this.unpark(this.parkedPurchase);
      this.parkedPurchase = null;
      this.step = 'size';
      this.stepPurchase.hidden = true;
      this.stepSize.hidden = false;
      this.overlay.classList.remove('dme-purchase-mode');
      if (this.drawerTitle && this.dataset.sizeTitle) this.drawerTitle.textContent = this.dataset.sizeTitle;
    }

    confirmSize() {
      if (!this.confirm || this.confirm.disabled) return;
      // Carry him to Add to Cart and Buy Now inside the drawer.
      if (this.showPurchaseStep()) return;
      // No purchase block on this template: this button is a plain add.
      this.closeSizes();
      this.submitOriginal();
    }

    onCartChanged() {
      // The drawer stands down the moment anything lands in the cart, from
      // ANY step and whichever button put it there (Add to Cart, Buy Now, or
      // a photo drawer elsewhere on the page). Once something is in the cart
      // the drawer has no business being on screen.
      this.cartChangedAt = Date.now();
      // The journey is finished. Forgetting the size means a stray tap on the
      // dock cannot put the same shirt in the cart a second time.
      this.selectedSize = null;
      if (this.overlay && !this.overlay.hidden) {
        this.showSizeStep();
        this.hideOverlay();
      } else {
        this.showSizeStep();
      }
      this.syncProductState();
    }

    recentlyAddedToCart() {
      // iOS can deliver a ghost tap to whatever sits under a surface that just
      // closed, and the dock pill sits directly under the cart notification.
      return Date.now() - (this.cartChangedAt || 0) < 900;
    }

    bindCartWatch() {
      const notify = () => this.onCartChanged();
      document.addEventListener('cart:refresh', notify);
      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, notify);
      }
      // Dawn marks its cart notification and cart drawer with .active when
      // either opens; that is the most reliable signal an add succeeded.
      document.querySelectorAll('cart-notification, cart-drawer').forEach((surface) => {
        const observer = new MutationObserver(() => notify());
        observer.observe(surface, { attributes: true, attributeFilter: ['class'] });
        this.cartSurfaceObservers.push(observer);
      });
    }

    bindProductActions() {
      this.action?.addEventListener('click', () => {
        if (this.recentlyAddedToCart()) return;
        if (this.action.dataset.mode === 'add') {
          this.openSizes();
          this.confirmSize();
        } else {
          this.openSizes();
        }
      });

      this.overlay?.addEventListener('click', (event) => {
        const sizeButton = event.target.closest('.dme-size-choice');
        if (sizeButton) {
          event.preventDefault();
          this.chooseSize(sizeButton);
          return;
        }
        if (event.target.closest('[data-dme-size-close]')) {
          event.preventDefault();
          this.closeSizes();
          return;
        }
        if (event.target.closest('[data-dme-size-help]')) {
          event.preventDefault();
          this.openSizeHelp();
          return;
        }
        if (event.target.closest('[data-dme-size-confirm]')) {
          event.preventDefault();
          this.confirmSize();
          return;
        }
        // Any tap landing on the overlay itself (outside the sheet) closes it.
        if (!event.target.closest('.dme-size-drawer')) this.closeSizes();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.overlay?.hidden) this.closeSizes();
      });

      // Dawn's ModalDialog.hide() fires this on the body for every close route:
      // the X, Escape and a tap on the backdrop.
      document.body.addEventListener('modalClosed', () => {
        if (!this.reopenAfterHelp) return;
        this.reopenAfterHelp = false;
        setTimeout(() => this.openSizes(), 60);
      });

      document.addEventListener('change', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
        if (!this.sizeFieldset()?.contains(target)) return;
        // Only a real customer action counts as a chosen size. Apps and the
        // theme fire synthetic change events on load; those must not make the
        // drawer claim a size the customer never picked.
        if (event.isTrusted) this.selectedSize = target.value;
        this.syncProductState();
      });

      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
        subscribe(PUB_SUB_EVENTS.variantChange, () => this.syncProductState());
      }

      this.bindCartWatch();

      const root = this.productRoot();
      if (root && root !== document) {
        this.productObserver = new MutationObserver(() => this.scheduleProductSync());
        this.productObserver.observe(root, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class', 'checked', 'value']
        });
      }
      this.syncProductState();
    }
  }

  customElements.define('dignity-mobile-experience', DignityMobileExperience);
}
